import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { MIN_TOPUP_CENTS } from "./wallet-constants";

/** Payout details may only be written with a session minted in the last 15 minutes (email OTP re-auth). */
const OTP_WINDOW_SECONDS = 15 * 60;

function assertFreshAuth(claims: unknown) {
  const iat = (claims as { iat?: number } | null)?.iat;
  if (!iat || Date.now() / 1000 - iat > OTP_WINDOW_SECONDS) {
    throw new Error("Verification expired. Request a new code and try again.");
  }
}

export const PAYOUT_CURRENCIES = ["EUR", "USD", "AED"] as const;
export type PayoutCurrency = (typeof PAYOUT_CURRENCIES)[number];

export const CRYPTO_ASSETS = ["USDT", "USDC"] as const;
export type CryptoAsset = (typeof CRYPTO_ASSETS)[number];
export const CRYPTO_NETWORK = "ERC20";

const payoutSchema = z
  .object({
    id: z.string().uuid().optional(),
    methodType: z.enum(["bank", "crypto"]),
    label: z.string().max(60).optional(),
    accountHolder: z.string().max(120).optional(),
    bankName: z.string().max(120).optional(),
    iban: z.string().max(40).optional(),
    bicSwift: z.string().max(20).optional(),
    bankCountry: z.string().max(2).optional(),
    bankAddress: z.string().max(200).optional(),
    intermediaryBank: z.string().max(200).optional(),
    cryptoAsset: z.enum(CRYPTO_ASSETS).optional(),
    cryptoAddress: z.string().max(120).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.methodType === "bank" && (!v.accountHolder || !v.iban || !v.bicSwift)) {
      ctx.addIssue({
        code: "custom",
        message: "Account holder, IBAN and BIC/SWIFT are required for bank payouts.",
      });
    }
    if (v.methodType === "crypto") {
      if (!v.cryptoAsset) {
        ctx.addIssue({ code: "custom", message: "Choose USDT or USDC." });
      }
      if (!v.cryptoAddress || !/^0x[a-fA-F0-9]{40}$/.test(v.cryptoAddress.trim())) {
        ctx.addIssue({
          code: "custom",
          message: "Enter a valid ERC20 wallet address starting with 0x.",
        });
      }
    }
  });


export const getWalletOverview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { getBalances } = await import("./wallets.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const balances = await getBalances(context.userId);
    const [{ data: methods }, { data: withdrawals }] = await Promise.all([
      supabaseAdmin
        .from("payout_methods")
        .select("*")
        .eq("user_id", context.userId)
        .order("created_at", { ascending: false }),
      supabaseAdmin
        .from("withdrawals")
        .select("id,amount_cents,currency,status,created_at,reference")
        .eq("user_id", context.userId)
        .order("created_at", { ascending: false })
        .limit(25),
    ]);
    return { balances, methods: methods ?? [], withdrawals: withdrawals ?? [] };
  });

export const getTransactions = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { listTransactions } = await import("./wallets.server");
    return { rows: await listTransactions(context.userId) };
  });

export const savePayoutMethod = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => payoutSchema.parse(input))
  .handler(async ({ data, context }) => {
    assertFreshAuth(context.claims);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const isCrypto = data.methodType === "crypto";
    const row = {
      user_id: context.userId,
      method_type: data.methodType,
      label: data.label ?? null,
      account_holder: isCrypto ? null : (data.accountHolder ?? null),
      bank_name: isCrypto ? null : (data.bankName ?? null),
      iban: !isCrypto && data.iban ? data.iban.replace(/\s+/g, "").toUpperCase() : null,
      bic_swift: !isCrypto && data.bicSwift ? data.bicSwift.replace(/\s+/g, "").toUpperCase() : null,
      bank_country:
        !isCrypto && data.bankCountry ? data.bankCountry.toUpperCase().slice(0, 2) : null,
      bank_address: isCrypto ? null : (data.bankAddress ?? null),
      intermediary_bank: isCrypto ? null : (data.intermediaryBank ?? null),
      crypto_asset: isCrypto ? (data.cryptoAsset ?? null) : null,
      crypto_network: isCrypto ? CRYPTO_NETWORK : null,
      crypto_address: isCrypto && data.cryptoAddress ? data.cryptoAddress.trim() : null,
      verified_at: new Date().toISOString(),
    };


    if (data.id) {
      const { error } = await supabaseAdmin
        .from("payout_methods")
        .update(row)
        .eq("id", data.id)
        .eq("user_id", context.userId);
      if (error) throw error;
      return { ok: true, id: data.id };
    }

    const { data: created, error } = await supabaseAdmin
      .from("payout_methods")
      .insert(row)
      .select("id")
      .single();
    if (error) throw error;
    return { ok: true, id: created.id };
  });

export const deletePayoutMethod = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    assertFreshAuth(context.claims);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("payout_methods")
      .delete()
      .eq("id", data.id)
      .eq("user_id", context.userId);
    if (error) throw error;
    return { ok: true };
  });

/** Minimum withdrawal: US$100.00 (bank transfer or USDT/USDC ERC20). */
export const MIN_WITHDRAWAL_USD = 100;
export const MIN_WITHDRAWAL_CENTS = MIN_WITHDRAWAL_USD * 433;


export const requestWithdrawal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        payoutMethodId: z.string().uuid(),
        amountCents: z.number().int().min(MIN_WITHDRAWAL_CENTS),
        currency: z.enum(PAYOUT_CURRENCIES).default("USD"),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { getBalances } = await import("./wallets.server");

    const { data: method } = await supabaseAdmin
      .from("payout_methods")
      .select("id,verified_at")
      .eq("id", data.payoutMethodId)
      .eq("user_id", context.userId)
      .maybeSingle();
    if (!method || !method.verified_at) {
      throw new Error("Add and verify a payout method first.");
    }

    const balances = await getBalances(context.userId);
    if (data.amountCents > balances.earnCents) {
      throw new Error("Amount exceeds your Earn wallet balance.");
    }

    const { data: withdrawal, error } = await supabaseAdmin
      .from("withdrawals")
      .insert({
        user_id: context.userId,
        payout_method_id: method.id,
        amount_cents: data.amountCents,
        currency: data.currency,
        status: "requested",
      })
      .select("id")
      .single();
    if (error) throw error;

    const { data: spent, error: spendError } = await supabaseAdmin.rpc("spend_wallet_credit", {
      _user_id: context.userId,
      _cents: data.amountCents,
      _reason: "withdrawal",
      _reference_type: "withdrawal",
      _reference_id: withdrawal.id,
      _wallet: "earn",
    });
    if (spendError) throw spendError;
    if (Number(spent ?? 0) !== data.amountCents) {
      await supabaseAdmin.from("withdrawals").update({ status: "cancelled" }).eq("id", withdrawal.id);
      throw new Error("Balance changed, please try again.");
    }

    return { ok: true, id: withdrawal.id };
  });




/** Top up the services wallet by card (AED). */
/**
 * Check a coupon before checkout. Heat coupons are credited straight into the
 * top-up wallet; percentage coupons return a discount to apply at checkout.
 */
export const applyTopupCoupon = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ code: z.string().min(3).max(40) }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { findUsableCoupon, redeemHeatCoupon } = await import("./coupons.server");
    const coupon = await findUsableCoupon(data.code, context.userId);
    if (coupon.kind === "heat") {
      const res = await redeemHeatCoupon(coupon, context.userId);
      return {
        kind: "heat" as const,
        code: coupon.code,
        heat: res.heat,
        percentOff: 0,
        message: `${res.heat} Heat added to your wallet.`,
      };
    }
    return {
      kind: "percent" as const,
      code: coupon.code,
      heat: 0,
      percentOff: coupon.percent_off,
      message: `${coupon.percent_off}% off your top-up.`,
    };
  });

export const createTopupCheckout = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        amountCents: z.number().int().min(MIN_TOPUP_CENTS).max(5_000_000),
        origin: z.string().url(),
        returnTo: z.string().optional(),
        couponCode: z.string().min(3).max(40).optional(),
      })
      .parse(input),
  )

  .handler(async ({ data, context }) => {
    const { getStripe } = await import("./stripe.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { TOPUP_BONUS_CENTS } = await import("./wallet-constants");

    // Every top-up adds 1 extra Heat on top of what was requested, so the
    // wallet always keeps a little spare balance for the next action.
    const creditCents = data.amountCents + TOPUP_BONUS_CENTS;

    // A percentage coupon lowers what we charge, the wallet is still credited in full.
    let discountCents = 0;
    let coupon: Awaited<ReturnType<typeof import("./coupons.server").findUsableCoupon>> | null =
      null;
    if (data.couponCode) {
      const { findUsableCoupon } = await import("./coupons.server");
      coupon = await findUsableCoupon(data.couponCode, context.userId);
      if (coupon.kind !== "percent" || coupon.percent_off <= 0) {
        throw new Error("This coupon cannot be applied to a top-up.");
      }
      discountCents = Math.min(
        creditCents - 100,
        Math.round((creditCents * coupon.percent_off) / 100),
      );
      if (discountCents < 0) discountCents = 0;
    }
    const chargeCents = creditCents - discountCents;

    const { data: order, error } = await supabaseAdmin
      .from("orders")
      .insert({
        user_id: context.userId,
        status: "pending_payment",
        kind: "topup",
        currency: "AED",
        boost_amount_cents: 0,
        credits_applied_cents: discountCents,
        amount_due_cents: creditCents,
      })

      .select("id")
      .single();
    if (error) throw error;

    if (coupon) {
      const { recordRedemption } = await import("./coupons.server");
      await recordRedemption(coupon, context.userId, discountCents, order.id);
    }

    const stripe = getStripe();
    const session = await stripe.checkout.sessions.create({
      mode: "payment",
      client_reference_id: order.id,
      success_url: `${data.origin}${data.returnTo && data.returnTo.startsWith("/") ? data.returnTo : "/app/wallets"}${(data.returnTo ?? "").includes("?") ? "&" : "?"}checkout=topup&session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${data.origin}${data.returnTo && data.returnTo.startsWith("/") ? data.returnTo : "/app/wallets"}`,
      metadata: { order_id: order.id, kind: "topup" },
      line_items: [
        {
          quantity: 1,
          price_data: {
            currency: "aed",
            unit_amount: chargeCents,
            product_data: {
              name: "Wallet top-up",
              description: coupon
                ? `Credit for BE HOT. NOW services — coupon ${coupon.code}`
                : "Credit for BE HOT. NOW services",
            },
          },
        },
      ],
    });


    await supabaseAdmin.from("orders").update({ stripe_session_id: session.id }).eq("id", order.id);
    if (!session.url) throw new Error("Stripe did not return a checkout URL.");
    return { url: session.url };
  });

/**
 * Safety net for a missed webhook: confirm the session with Stripe on return
 * from checkout and fulfil the order if it is paid. Idempotent.
 */
export const verifyCheckoutSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ sessionId: z.string().min(10).max(200) }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { getStripe } = await import("./stripe.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { fulfilBoostForSession } = await import("./boosts.server");

    const { data: order } = await supabaseAdmin
      .from("orders")
      .select("id,user_id,status")
      .eq("stripe_session_id", data.sessionId)
      .maybeSingle();
    if (!order || order.user_id !== context.userId) return { ok: false, status: "unknown" };
    if (order.status === "paid") return { ok: true, status: "paid" };

    const session = await getStripe().checkout.sessions.retrieve(data.sessionId);
    if (session.payment_status !== "paid") return { ok: false, status: session.payment_status };

    const pi = typeof session.payment_intent === "string" ? session.payment_intent : null;
    await fulfilBoostForSession(data.sessionId, pi);
    return { ok: true, status: "paid" };
  });
